<template>
  <AuthPageWrapper
    :eventId="eventId"
    :eventLogo="eventLogo"
    :altText="eventTitle"
    :eventTitle="eventTitle"
    :eventDate="eventDate"
    :bannerView="themedAuth"
    :isLoginAvailable="allowLogin || hasCustomContent"
    :isEventTitleVisible="isEventTitleVisible"
    :loading="loading"
    :isEventDeleted="isEventDeleted"
    :isEventError="isEventError"
  >
    <template v-if="hasCustomContent" #default><slot /></template>
  </AuthPageWrapper>
</template>

<script>
import { authorizeOauth2 } from '@goldcast/api/backend';
import { AuthPageWrapper } from 'uicomponents';
import { mapGetters, mapActions } from 'vuex';

import goldcastLogo from '@/assets/logo.svg';
import loggerService from '@/services/logger.service';
import { startToEndDateString, getStartToEndDateInEventLocale } from '@/utils/dateTime';
import { redirectToOnDemandExperienceIfApplicable } from '@/utils/onDemandRedirect';

export default {
  name: 'AuthWrapper',
  components: {
    AuthPageWrapper
  },
  data() {
    return {
      mode: 'NO_IMAGE',
      largeBannerUrl:
        'https://firebasestorage.googleapis.com/v0/b/investor-newz.appspot.com/o/banner_large.jpg?alt=media&token=55b5dbbe-1ae2-402e-bced-2dcfcde615cf',
      smallBannerUrl:
        'https://firebasestorage.googleapis.com/v0/b/investor-newz.appspot.com/o/banner_small.jpg?alt=media&token=ccb1bcb2-7bd4-4932-9b37-55d84ec3c463',
      eventLogo: null,
      eventBannerImage: null,
      loading: true,
      eventName: null,
      orgName: null,
      isEventDeleted: false,
      isEventError: false
    };
  },
  created() {
    document.title = '@@translate@@ Goldcast';
    if (this.eventId) {
      this.getEvent().then(() => {
        this.$store.dispatch('event/setAppDataOnBrowser');
      });
    } else {
      this.loading = false;
      this.RESET();
      this.eventLogo = goldcastLogo;
    }
  },
  computed: {
    ...mapGetters('event', ['currentEvent', 'allowLogin']),
    themedAuth() {
      return !!(this.currentEvent && this.eventId);
    },
    eventId() {
      return this.$route.query.eventID || this.$route.params?.eventID;
    },
    hasCustomContent() {
      return !!this.$slots.default;
    },
    eventTitle() {
      return this.currentEvent?.title;
    },
    eventDate() {
      if (!this.currentEvent?.start_time || !this.currentEvent?.end_time) {
        return '';
      }

      if (this.$store.state.event?.useEventLocaleForDateTimeFormatting) {
        return getStartToEndDateInEventLocale({
          startDate: new Date(this.currentEvent?.start_time),
          endDate: new Date(this.currentEvent?.end_time),
          locale: this.currentEvent?.locale
        });
      }

      return startToEndDateString(new Date(this.currentEvent?.start_time), new Date(this.currentEvent?.end_time));
    },
    isEventTitleVisible() {
      return this.currentEvent?.is_title_visible;
    },
    isEmbeddedApp() {
      return this.$route.query?.isEmbedded === 'true';
    }
  },
  methods: {
    ...mapActions('event', ['loadPublicEvent', 'RESET']),
    async getEvent() {
      this.isEventDeleted = false;
      this.isEventError = false;
      try {
        const uberResponse = await this.loadPublicEvent({ eventID: this.eventId });

        if (this.currentEvent && uberResponse) {
          const isComingFromEventAppMagicLink = window.location.pathname.includes('/auth/link/');

          if (
            !this.hasCustomContent &&
            !isComingFromEventAppMagicLink &&
            !this.isEmbeddedApp &&
            redirectToOnDemandExperienceIfApplicable({ store: this.$store, route: this.$route })
          ) {
            return;
          }

          // Cvent OAuth: if the event is linked to Cvent, redirect to Cvent login.
          // User is on the auth page because auth failed (tokens missing or expired) — redirect either way.
          // Skip if user arrived via magic link, SSO, or any other auth sub-route (they already have a login mechanism).
          const isOnBaseAuthPage = this.$route.name === 'entry';

          if (isOnBaseAuthPage && this.currentEvent.cvent_event_id) {
            try {
              loggerService.log('[CventOAuth] Event is Cvent-linked, attempting OAuth redirect', {
                eventId: this.eventId,
                cvent_event_id: this.currentEvent.cvent_event_id
              });
              const response = await authorizeOauth2({
                gc_event_id: this.eventId,
                queryParams: {}
              });
              if (response.redirectUrl) {
                loggerService.log('[CventOAuth] Redirecting to Cvent login', response.redirectUrl);
                globalThis.location.href = response.redirectUrl;
                return;
              }
              loggerService.error('[CventOAuth] No redirectUrl in response', {
                hasRedirectUrl: Boolean(response?.redirectUrl)
              });
            } catch (err) {
              loggerService.error('Cvent OAuth redirect failed on auth page', { error: err?.message || err });
            }
          }

          this.eventLogo = this.currentEvent && this.currentEvent.logo_url ? this.currentEvent.logo_url : null;

          this.eventBannerImage =
            this.currentEvent && this.currentEvent.banner_url ? this.currentEvent.banner_url : null;

          if (this.currentEvent.themeLayout) {
            this.mode = this.currentEvent.theme_layout;
          }
        } else {
          this.eventLogo = goldcastLogo;
        }
      } catch (e) {
        const isEventNotFoundError = e?.statusCode === 404 && e?.apiErrorCode === 'not_found';
        if (isEventNotFoundError) {
          this.isEventDeleted = true;
        } else {
          this.isEventError = true;
        }
        loggerService.warn('error in AuthWrapper.vue getEvent - check if event exists!', e);
      }
      // Redirect branches return early so the loader stays up: a full-page redirect keeps this
      // page visible until the target responds, and the sign-in form would otherwise flash.
      this.loading = false;
    }
  }
};
</script>

<style scoped></style>
